"use strict";
/**
 * Pad 2's one press and hold (ai/nba/N8-taps/PLAN.md §19.5) for resume, Exit, Stop tracking and the
 * period button. The pad's delegated pointer listeners drive it; it attaches none of its own.
 *
 * Renderer contract: hold targets carry `data-hold` (set by `prepareHoldTarget`, which also sets what
 * can be set inline); while a hold runs the target has `HOLD_FILL_CLASS` and `--hold-ms`, so a fill
 * can transition over exactly the hold. A stylesheet should add `-webkit-touch-callout: none` on
 * `[data-hold]`, which inline styles cannot carry in every engine.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.HoldGesture = exports.HOLD_FILL_CLASS = exports.HOLD_MOVE_PX = exports.HOLD_MS = void 0;
exports.prepareHoldTarget = prepareHoldTarget;
exports.HOLD_MS = 600;
/** A press that travels further than this (CSS px) is a slide, not a hold. */
exports.HOLD_MOVE_PX = 10;
exports.HOLD_FILL_CLASS = "hold-filling";
/** Touch-action must be in place before the press starts, so this runs at render. */
function prepareHoldTarget(el) {
    el.dataset.hold = "";
    el.style.touchAction = "none";
    el.style.setProperty("user-select", "none");
    el.style.setProperty("-webkit-user-select", "none");
    el.style.setProperty("-webkit-touch-callout", "none");
    el.style.setProperty("--hold-ms", `${exports.HOLD_MS}ms`);
}
const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
class HoldGesture {
    constructor(opts) {
        this.opts = opts;
        this.timer = null;
        this.el = null;
        this.pointerId = null;
        this.x = 0;
        this.y = 0;
        /** The click that follows a completed hold is not a tap (after Exit the picker is under the finger). */
        this.swallow = false;
    }
    /**
     * Every pointerdown on the pad, before the press's own action. Starts a hold when `target` has a
     * spec whose control is already in its hold state; true when one started.
     */
    press(event, target, spec) {
        this.swallow = false;
        if (target === null || spec === undefined || !spec.inHoldState())
            return false;
        event.preventDefault(); // no callout or selection taking the press over
        this.reset();
        const p = event;
        this.el = target;
        this.pointerId = p.pointerId ?? null;
        this.x = num(p.clientX);
        this.y = num(p.clientY);
        target.style.setProperty("--hold-ms", `${exports.HOLD_MS}ms`);
        target.classList.add(exports.HOLD_FILL_CLASS);
        const id = window.setTimeout(() => {
            this.opts.timers.delete(id);
            this.timer = null;
            target.classList.remove(exports.HOLD_FILL_CLASS);
            if (!this.opts.isCurrent())
                return this.reset();
            this.swallow = true;
            spec.onHold();
        }, exports.HOLD_MS);
        this.timer = id;
        this.opts.timers.add(id);
        return true;
    }
    mine(event) {
        return this.el !== null && (event.pointerId ?? null) === this.pointerId;
    }
    /** pointermove: a slide beyond the threshold ends the hold. */
    move(event) {
        if (!this.mine(event) || this.timer === null)
            return;
        const p = event;
        if (Math.hypot(num(p.clientX) - this.x, num(p.clientY) - this.y) > exports.HOLD_MOVE_PX)
            this.reset();
    }
    /** pointerup: ends the press; a hold already completed has fired. */
    release(event) {
        if (this.mine(event))
            this.reset();
    }
    /** pointercancel: ends the hold only for the pressing pointer. */
    cancel(event) {
        if (this.mine(event))
            this.reset();
    }
    /** click (listen in the capture phase): true, and the click stopped, when it follows a completed hold. */
    swallowClick(event) {
        if (!this.swallow)
            return false;
        this.swallow = false;
        event.preventDefault();
        event.stopPropagation();
        return true;
    }
    reset() {
        if (this.timer !== null) {
            window.clearTimeout(this.timer);
            this.opts.timers.delete(this.timer);
        }
        this.timer = null;
        this.el?.classList.remove(exports.HOLD_FILL_CLASS);
        this.el = null;
        this.pointerId = null;
    }
}
exports.HoldGesture = HoldGesture;
